import { Plus, Pencil, Trash2, CalendarRange, AlertTriangle } from "lucide-react";
import { formatDateIt, isToday, placeOf } from "../../lib/constants";
import { Group, IconBtn } from "./AdminUI";
import { RecurrenceBadge } from "../RecurrenceBadge";

const todayStr = () => new Date().toLocaleDateString("sv-SE");
const shownDate = (e) => (e.recurrence_label ? e.next_date : e.date);

const ConfirmBanner = ({ e, onConfirm, onArchive }) => (
  <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg bg-wyd-peach/10 border border-wyd-peach/30 px-3 py-2 text-sm" data-testid={`admin-confirm-banner-${e.id}`}>
    <span className="flex items-center gap-1.5 text-wyd-peach"><AlertTriangle size={14} /> Questa serata c'è ancora?</span>
    {e.hidden_unconfirmed && <span className="text-xs text-wyd-muted">Nascosta dalla home finché non confermi</span>}
    <div className="flex gap-2 ml-auto">
      <button data-testid={`admin-confirm-yes-${e.id}`} onClick={() => onConfirm(e.id)} className="px-3 py-1 rounded-full bg-wyd-accent text-wyd-bg font-semibold text-xs">Sì, confermo</button>
      <button data-testid={`admin-confirm-no-${e.id}`} onClick={() => onArchive(e.id)} className="px-3 py-1 rounded-full border border-wyd-accent text-wyd-accent font-semibold text-xs">Non c'è più</button>
    </div>
  </div>
);

const EventRow = ({ e, onEdit, onDelete, onDates, onConfirm, onArchive }) => {
  const d = shownDate(e);
  const details = [e.category, placeOf(e), d ? formatDateIt(d) : "nessuna data nei prossimi 60 giorni"].filter(Boolean).join(" · ");
  return (
    <div className="glass rounded-xl p-4" data-testid={`admin-event-row-${e.id}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="font-display font-bold truncate">
            {e.title}{isToday(d) && <span className="ml-2 text-xs text-wyd-accent">· Stasera</span>}
          </div>
          <div className="text-sm text-wyd-muted">{details}</div>
          <RecurrenceBadge label={e.recurrence_label} testid={`admin-recurrence-${e.id}`} className="mt-1" />
        </div>
        <div className="flex gap-2 shrink-0">
          {e.recurrence_label && (
            <IconBtn testid={`admin-dates-${e.id}`} label="Gestisci date" onClick={() => onDates(e)} className="bg-wyd-surface2 text-wyd-muted hover:text-white"><CalendarRange size={16} /></IconBtn>
          )}
          <IconBtn testid={`admin-edit-${e.id}`} label="Modifica" onClick={() => onEdit(e)} className="bg-wyd-surface2 text-wyd-muted hover:text-white"><Pencil size={16} /></IconBtn>
          <IconBtn testid={`admin-delete-${e.id}`} label="Elimina" onClick={() => onDelete(e.id)} className="bg-wyd-surface2 text-wyd-muted hover:text-red-400"><Trash2 size={16} /></IconBtn>
        </div>
      </div>
      {e.needs_confirmation && <ConfirmBanner e={e} onConfirm={onConfirm} onArchive={onArchive} />}
    </div>
  );
};

export const AdminEvents = ({ events, onNew, ...actions }) => {
  const today = todayStr();
  const upcoming = events.filter((e) => (shownDate(e) || "") >= today);
  const past = events.filter((e) => (shownDate(e) || "") < today);
  return (
    <section data-testid="admin-events-section">
      <div className="flex items-center justify-between gap-4 mb-6">
        <h2 className="font-display font-bold text-2xl">Eventi</h2>
        <button data-testid="admin-new-event" onClick={onNew}
          className="flex items-center gap-2 px-6 py-3 rounded-full bg-wyd-accent text-wyd-bg font-semibold hover:glow-magenta transition-shadow duration-300">
          <Plus size={18} /> Nuovo evento
        </button>
      </div>
      <Group title="In programma" count={upcoming.length} testid="admin-events-upcoming" empty="Nessun evento in programma.">
        {upcoming.map((e) => <EventRow key={e.id} e={e} {...actions} />)}
      </Group>
      <Group title="Passati" count={past.length} testid="admin-events-past" empty="Nessun evento passato.">
        {past.map((e) => <EventRow key={e.id} e={e} {...actions} />)}
      </Group>
    </section>
  );
};
